import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A component that displays a title and content, where the content
 * can be shown or hidden by clicking the title area.
 * Includes a chevron icon to indicate the open/closed state.
 * Uses semantic styling and configuration support.
 *
 * @component
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.title - The title of the collapsible section.
 * @param {React.ReactNode} props.children - The content to be shown/hidden.
 * @param {boolean} [props.initialOpen=false] - Whether the section should be open initially.
 * @param {string} [props.className] - Optional additional CSS classes for the main wrapper div.
 * @param {string} [props.titleClassName] - Optional additional CSS classes for the title button.
 * @returns {React.ReactElement} The rendered CollapsibleSection component.
 *
 * @example
 * <CollapsibleSection title="Advanced Options">
 *   <p>Some advanced settings here.</p>
 * </CollapsibleSection>
 */
const CollapsibleSection = ({
    title,
    children,
    initialOpen = false,
    className = '',
    titleClassName = ''
}) => {
    const colors = useColors();
    const semantic = colors.semantic;
    const { replace } = useTextReplacement();
    const [isOpen, setIsOpen] = useState(initialOpen);

    const toggleOpen = () => setIsOpen(!isOpen);

    return (
        <div className={`border-t ${semantic.contentCardBorder} pt-4 ${className}`}>
            <button
                type="button"
                onClick={toggleOpen}
                className={`flex justify-between items-center w-full font-semibold ${semantic.contentCardHeading} mb-3 ${titleClassName}`}
                aria-expanded={isOpen}
            >
                {typeof title === 'string' ? replace(title) : title}
                {isOpen ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {isOpen && (
                <div className="mt-4">
                    {children}
                </div>
            )}
        </div>
    );
};

CollapsibleSection.propTypes = {
    title: PropTypes.node.isRequired,
    children: PropTypes.node.isRequired,
    initialOpen: PropTypes.bool,
    className: PropTypes.string,
    titleClassName: PropTypes.string,
};

export default CollapsibleSection;

